import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Switch } from '@/shared/ui/switch';
import { Loader2, Server } from 'lucide-react';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { ServiceAccount } from '@/shared/types/serviceAccountTypes';
import { useUpdateAccountServiceAccount } from '@/domains/service-accounts/services/account-service-accounts';

interface ManageServiceAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  serviceAccount: ServiceAccount | null;
}

const ManageServiceAccountDialog: React.FC<ManageServiceAccountDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  serviceAccount,
}) => {
  // Initialize from serviceAccount so controlled Switch never mounts with mismatched value (avoids flushSync warning from Radix)
  const [name, setName] = useState(() => serviceAccount?.name ?? '');
  const [status, setStatus] = useState(() => !!serviceAccount?.status);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const updateServiceAccount = useUpdateAccountServiceAccount();

  useEffect(() => {
    if (isOpen && serviceAccount) {
      setName(serviceAccount.name || '');
      setStatus(!!serviceAccount.status);
      setErrors({});
    }
  }, [isOpen, serviceAccount]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceAccount || !validateForm()) return;

    try {
      await updateServiceAccount.mutateAsync({
        serviceAccountId: serviceAccount.clientId,
        serviceAccountData: { name: name.trim(), status },
      });
      showSuccess(SUCCESS.SERVICE_ACCOUNT.UPDATED);
      onSuccess?.();
      onClose();
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_ACCOUNT.UPDATE);
    }
  };

  const hasChanges = () => {
    if (!serviceAccount) return false;
    const nameChanged = name.trim() !== (serviceAccount.name || '');
    const statusChanged = status !== !!serviceAccount.status;
    return nameChanged || statusChanged;
  };

  const canSubmit = !updateServiceAccount.isPending && !!serviceAccount && hasChanges() && name.trim().length >= 2;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Manage Service Account</DialogTitle>
          <DialogDescription>
            Update the name and status of this service account.
          </DialogDescription>
        </DialogHeader>

        {serviceAccount && (
          <>
            <div className="flex items-center gap-2 border-b pb-3">
              <Server className="h-5 w-5" />
              <div>
                <p className="text-sm font-medium">{serviceAccount.name}</p>
                <p className="text-xs">ID: {serviceAccount.clientId}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="manage-service-account-name">Name *</Label>
                <Input
                  id="manage-service-account-name"
                  type="text"
                  placeholder="Service account name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="manage-service-account-status">Active</Label>
                  <p className="text-sm">
                    When inactive, this service account cannot be used for API access.
                  </p>
                </div>
                <Switch
                  id="manage-service-account-status"
                  checked={status}
                  onCheckedChange={setStatus}
                />
              </div>

              <DialogFooter>
                <Button type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={updateServiceAccount.isPending}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSubmit}>
                  {updateServiceAccount.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageServiceAccountDialog;
